/**
 * Quanto spazio ha il menu di una tendina, e da che parte aprirlo.
 *
 * ── Perche' esiste ─────────────────────────────────────────────────────────
 *
 * Il menu di `VariantSelect` e' in posizione assoluta e non sta in un portale,
 * quindi lo ritaglia l'antenato piu' vicino che dichiara `overflow`. La
 * decisione su dove aprirlo, prima, misurava lo SCHERMO — e lo schermo non e'
 * il riquadro che ritaglia. Dentro un foglio a tutto schermo il corpo che
 * scorre finisce 99px piu' in alto del bordo della finestra, perche' sotto c'e'
 * la barra appiccicata con la stima e il pulsante d'invio: il menu credeva di
 * avere spazio, si apriva in giu', e veniva mozzato.
 *
 * Misurato nel browser vero il 19 settembre 2026, tendina «Inizia Tra» del
 * modale di creazione torneo, larghezza 360:
 *
 * - il menu di sei voci e' alto **278px** (6 x 44 piu' i bordi);
 * - gliene servono **284** contando lo stacco di 6 dal pulsante;
 * - il vecchio conto ne stimava **252** (`voci * 40 + 12`), 32 in meno del vero;
 * - il fondo di `.play-online-body` sta **99px** sopra il fondo della finestra.
 *
 * Da cui: si apriva in giu' appena lo spazio-schermo arrivava a 252, ma perche'
 * ci stesse davvero ne servivano 383. Nella fascia fra i due il menu veniva
 * tagliato, fino a 131px — due o tre voci. La formula e' stata verificata
 * contro la misura: a 360x900 lo spazio sotto era 376, il taglio previsto
 * 383 - 376 = 7px, e il menu misurava 808 contro il fondo del corpo a 801.
 *
 * ── Che cosa fa adesso ─────────────────────────────────────────────────────
 *
 * Il menu non deve uscire dal suo riquadro e non deve allungare il modale: se
 * lo spazio non basta, **scorre dentro di se'**. Per questo qui non si decide
 * solo il verso, si restituisce anche l'ALTEZZA MASSIMA — che il foglio
 * trasforma in una barra di scorrimento, perche' `.variant-select-menu` ha gia'
 * `overflow-y: auto`.
 *
 * Funzione pura e provata a parte: la geometria e' la parte che sbaglia, e in
 * jsdom i rettangoli sono tutti zeri, quindi dentro un banco di prova di
 * componente non si misura niente.
 */

/** Un riquadro, ridotto alla sola verticale, in coordinate di finestra. */
export interface Fascia {
  top: number;
  bottom: number;
}

/** Lo stacco fra pulsante e menu, dal foglio: `top: calc(100% + 6px)`. */
export const STACCO = 6;
/** Il respiro dal bordo di chi ritaglia: il menu non si appiccica al filo. */
export const MARGINE = 8;
/** Il tetto del foglio (`max-height: 320px`). Non si supera mai. */
export const TETTO = 320;
/**
 * Sotto due voci un menu non e' piu' un menu: si preferisce sbordare di poco
 * piuttosto che consegnare una fessura in cui non si legge niente. Ci si
 * arriva solo quando NESSUNO dei due versi ha spazio, che vuol dire un
 * contenitore piu' basso di ~100px.
 */
export const PAVIMENTO = 88;
/** L'altezza di una voce, misurata: 9 + 9 di riempimento piu' la riga. */
export const VOCE = 44;

/**
 * Da che parte aprire il menu, e quanto alto farlo.
 *
 * @param pulsante il riquadro del pulsante che apre la tendina
 * @param ritaglio il riquadro che ritaglia davvero — il contenitore che
 *   scorre, gia' intersecato con la finestra
 * @param voci quante righe ha il menu
 */
export function decidiMenu(
  pulsante: Fascia,
  ritaglio: Fascia,
  voci: number,
): { su: boolean; altezzaMassima: number } {
  const sotto = ritaglio.bottom - pulsante.bottom - STACCO - MARGINE;
  const sopra = pulsante.top - ritaglio.top - STACCO - MARGINE;
  /* Quanto vorrebbe: le sue voci, mai oltre il tetto del foglio. */
  const serve = Math.min(TETTO, voci * VOCE + 12);
  /*
   * In alto solo se sotto non ci sta E sopra c'e' piu' posto. Quando non ci
   * sta da nessuna delle due parti si sceglie il lato piu' largo, e il menu
   * scorre: e' sempre meglio di un menu mozzato, che non dice nemmeno di
   * essere mozzato.
   */
  const su = sotto < serve && sopra > sotto;
  const spazio = su ? sopra : sotto;
  return { su, altezzaMassima: Math.min(TETTO, Math.max(PAVIMENTO, spazio)) };
}

/**
 * Il riquadro che ritaglia davvero un elemento: il primo antenato che dichiara
 * `overflow`, intersecato con la finestra.
 *
 * L'intersezione NON e' pedanteria: in questa applicazione il contenitore che
 * scorre la pagina e' `document.body` (vedi la nota di architettura), il cui
 * rettangolo e' alto quanto TUTTO il documento — senza intersecare, una tendina
 * in fondo alla pagina si crederebbe libera di aprirsi per duemila pixel.
 *
 * Sta qui e non nella funzione pura perche' tocca il DOM: in jsdom i
 * rettangoli sono zeri e `getComputedStyle` risponde `visible` per tutti,
 * quindi si ricade sulla finestra e il menu si apre in giu' alto come il
 * foglio — cioe' esattamente come prima di questa riparazione.
 */
export function fasciaCheRitaglia(elemento: HTMLElement): Fascia {
  const finestra = { top: 0, bottom: window.innerHeight };
  let antenato = elemento.parentElement;
  while (antenato) {
    const stile = getComputedStyle(antenato);
    if (stile.overflowY !== 'visible' || stile.overflowX !== 'visible') {
      const r = antenato.getBoundingClientRect();
      return {
        top: Math.max(finestra.top, r.top),
        bottom: Math.min(finestra.bottom, r.bottom),
      };
    }
    antenato = antenato.parentElement;
  }
  return finestra;
}
